import { useCallback, useEffect, useRef, useState } from 'react'

interface SwipeOptions {
  /** True while something else owns the gesture, such as a card being dragged. */
  blocked?: () => boolean
}

/**
 * A sideways swipe on an element moves a period: left for the next, right for the previous. The element
 * follows the finger; let go past a third of its width, or with a flick, and it slides away, the period
 * changes, and the new one slides in from the other side; short of that it springs back.
 *
 * Returns a ref callback, so the swipe attaches whenever the element appears: a view that first draws a
 * loading placeholder still gets it once its content arrives.
 *
 * Only a quick, mostly sideways gesture counts. A press held still first is left to drag and drop, and
 * a vertical one to scrolling; once a gesture is judged sideways the page stops scrolling with it.
 * A press on a line that scrolls sideways itself (marked `data-swipe-scroller`) scrolls that line, as
 * long as it has more than fits.
 */
export function useSwipe(onLeft: () => void, onRight: () => void, options: SwipeOptions = {}): (el: HTMLElement | null) => void {
  const [el, setEl] = useState<HTMLElement | null>(null)
  const handlers = useRef({ onLeft, onRight, blocked: options.blocked })
  useEffect(() => {
    handlers.current = { onLeft, onRight, blocked: options.blocked }
  })

  useEffect(() => {
    if (!el) return
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let startX = 0
    let startY = 0
    let startAt = 0
    let lastX = 0
    let lastAt = 0
    let speed = 0
    // null until the first few pixels decide: a swipe, or anything else
    let swiping: boolean | null = null
    let settling = 0

    const setX = (x: number, ms = 0, fade = false) => {
      el.style.transition = ms ? `transform ${ms}ms ease-out, opacity ${ms}ms ease-out` : 'none'
      el.style.transform = x ? `translateX(${x}px)` : ''
      el.style.opacity = fade ? '0' : ''
    }

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1 || handlers.current.blocked?.() || inScroller(e.target)) {
        swiping = false
        return
      }
      const t = e.touches[0]
      startX = lastX = t.clientX
      startY = t.clientY
      startAt = lastAt = e.timeStamp
      speed = 0
      swiping = null
    }

    const onMove = (e: TouchEvent) => {
      if (swiping === false) return
      if (handlers.current.blocked?.()) {
        if (swiping) setX(0, 160)
        swiping = false
        return
      }
      const t = e.touches[0]
      const dx = t.clientX - startX
      const dy = t.clientY - startY
      if (swiping === null) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return
        // a press held still first belongs to dragging; a mostly vertical move to scrolling
        swiping = Math.abs(dx) > Math.abs(dy) * 1.2 && e.timeStamp - startAt < 300
        if (!swiping) return
      }
      if (e.cancelable) e.preventDefault()
      speed = (t.clientX - lastX) / Math.max(1, e.timeStamp - lastAt)
      lastX = t.clientX
      lastAt = e.timeStamp
      if (!reduced) setX(dx)
    }

    const onEnd = () => {
      if (!swiping) {
        swiping = null
        return
      }
      swiping = null
      const dx = lastX - startX
      const width = el.offsetWidth || window.innerWidth
      const go = Math.abs(dx) > width / 3 || (Math.abs(dx) > 40 && Math.abs(speed) > 0.4)
      if (!go) {
        if (!reduced) setX(0, 180)
        return
      }
      const toNext = dx < 0
      if (reduced) {
        if (toNext) handlers.current.onLeft()
        else handlers.current.onRight()
        return
      }
      // out with the finger, change the period, then in from the far side
      setX(toNext ? -width : width, 150, true)
      window.clearTimeout(settling)
      settling = window.setTimeout(() => {
        if (toNext) handlers.current.onLeft()
        else handlers.current.onRight()
        setX(toNext ? width * 0.2 : -width * 0.2, 0, true)
        requestAnimationFrame(() => requestAnimationFrame(() => setX(0, 180)))
      }, 150)
    }

    el.addEventListener('touchstart', onStart, { passive: true })
    // not passive: once a gesture is judged sideways, the page must not scroll with it
    el.addEventListener('touchmove', onMove, { passive: false })
    el.addEventListener('touchend', onEnd, { passive: true })
    el.addEventListener('touchcancel', onEnd, { passive: true })
    return () => {
      window.clearTimeout(settling)
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', onEnd)
      el.removeEventListener('touchcancel', onEnd)
      el.style.transition = ''
      el.style.transform = ''
      el.style.opacity = ''
    }
  }, [el])

  return useCallback((node: HTMLElement | null) => setEl(node), [])
}

/** Whether a press landed on a sideways scroller with something to scroll; one that all fits leaves the swipe alone. */
function inScroller(target: EventTarget | null): boolean {
  const line = target instanceof Element ? target.closest<HTMLElement>('[data-swipe-scroller]') : null
  return !!line && line.scrollWidth > line.clientWidth + 1
}
